/**
 * AuthBrandingPanel — Painel de branding lateral (lado esquerdo) reutilizado
 * pela tela de Login (`/auth`) e pela tela de Seleção de Contexto
 * (`/select-context`). Encapsula gradiente, blobs decorativos, animação
 * Lottie, badges flutuantes, versículo bíblico rotacionado e rodapé.
 *
 * Markup extraído 1:1 do arquivo `src/pages/Auth.tsx` para preservar
 * exatamente o mesmo visual já validado em produção.
 *
 * CHANGELOG:
 *   v1.0.0 — 2026-05-29 — Extração inicial do painel para uso compartilhado.
 */
import { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { Quote } from 'lucide-react';
import chatSupportAnimation from '@/assets/animations/chat-support.json';

// Versículos NTLH — mantidos idênticos ao Auth.tsx original
const versiculosBiblia = [
  { texto: 'Confie no SENHOR de todo o coração e não se apoie na sua própria inteligência.', referencia: 'Provérbios 3:5' },
  { texto: 'Eu sei muito bem os planos que tenho para vocês, planos de paz e não de desgraça, planos para dar a vocês um futuro e uma esperança.', referencia: 'Jeremias 29:11' },
  { texto: 'O SENHOR é o meu pastor: nada me faltará.', referencia: 'Salmos 23:1' },
  { texto: 'Tudo posso naquele que me fortalece.', referencia: 'Filipenses 4:13' },
  { texto: 'Não tenha medo, pois eu estou com você; não fique aflito, pois eu sou o seu Deus. Eu dou forças a você e o ajudo.', referencia: 'Isaías 41:10' },
  { texto: 'Entreguem todas as suas preocupações a Deus, pois ele cuida de vocês.', referencia: '1 Pedro 5:7' },
  { texto: 'O amor é paciente, é bondoso. O amor não é ciumento, nem orgulhoso, nem vaidoso.', referencia: '1 Coríntios 13:4' },
  { texto: 'Porque Deus amou o mundo tanto, que deu o seu único Filho, para que todo aquele que nele crer não morra, mas tenha a vida eterna.', referencia: 'João 3:16' },
  { texto: 'Peçam, e vocês receberão; busquem, e vocês acharão; batam, e a porta será aberta.', referencia: 'Mateus 7:7' },
  { texto: 'A fé é a certeza de que vamos receber as coisas que esperamos e a prova de que existem coisas que não podemos ver.', referencia: 'Hebreus 11:1' },
  { texto: 'Venham a mim, todos vocês que estão cansados de carregar as suas pesadas cargas, e eu lhes darei descanso.', referencia: 'Mateus 11:28' },
  { texto: 'O SENHOR abençoe você e o guarde; o SENHOR faça brilhar o seu rosto sobre você e tenha misericórdia de você.', referencia: 'Números 6:24-25' },
];

// Badges flutuantes ao redor da animação Lottie
const floatingBadges = [
  { text: 'Omnichannel', position: 'top-16 left-8', delay: '0s' },
  { text: 'Automações', position: 'top-32 right-12', delay: '0.5s' },
  { text: 'Métricas', position: 'bottom-40 left-16', delay: '1s' },
  { text: 'Segurança', position: 'bottom-24 right-8', delay: '1.5s' },
];

export function AuthBrandingPanel() {
  // Estado de versículo (rotaciona a cada 2 minutos, idêntico ao Auth)
  const [versiculoIndex, setVersiculoIndex] = useState(() =>
    Math.floor(Math.random() * versiculosBiblia.length)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setVersiculoIndex((prev) => {
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * versiculosBiblia.length);
        } while (newIndex === prev && versiculosBiblia.length > 1);
        return newIndex;
      });
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  const versiculoAtual = versiculosBiblia[versiculoIndex];

  return (
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
      {/* Gradiente azul/turquesa elegante compatível com a logo */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a2540] via-[#1a3a5c] to-[#0d4a6b]" />

      {/* Overlay com padrão sutil de pontos */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 2px, transparent 2px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Blobs decorativos flutuantes */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-[#2dd4bf]/20 rounded-full blur-xl animate-auth-float" />
      <div className="absolute bottom-32 right-20 w-40 h-40 bg-[#3b82f6]/20 rounded-full blur-xl animate-auth-float-delayed" />
      <div className="absolute top-1/2 left-10 w-24 h-24 bg-[#2dd4bf]/15 rounded-full blur-lg animate-auth-float" />
      <div className="absolute top-40 right-32 w-20 h-20 bg-[#60a5fa]/15 rounded-full blur-lg animate-auth-float-delayed" />

      {/* Conteúdo principal: badges + animação + versículo */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-8">
        {floatingBadges.map((badge, index) => (
          <div
            key={index}
            className={`absolute ${badge.position} px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white/90 text-sm font-medium shadow-lg animate-auth-float`}
            style={{ animationDelay: badge.delay }}
          >
            {badge.text}
          </div>
        ))}

        <div className="w-full max-w-md flex-shrink-0">
          <Lottie animationData={chatSupportAnimation} loop className="w-full h-auto" />
        </div>

        <div className="mt-6 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-500 max-w-md w-full">
          <div className="flex items-start gap-2">
            <Quote className="h-5 w-5 text-[#2dd4bf] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white/90 italic text-sm leading-relaxed">
                "{versiculoAtual.texto}"
              </p>
              <p className="text-[#2dd4bf] text-xs mt-2 font-medium">
                — {versiculoAtual.referencia}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-white/20 max-w-md w-full">
          <p className="text-white/70 text-xs text-center">
            Mais de 1.000 empresas confiam no UaizyChat para transformar seu atendimento
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthBrandingPanel;
